"use client";

import Image from "next/image";
import dayjs from "dayjs";
import { useWindowSize } from "../../hooks/useWindowSize";
import { motion } from "framer-motion";
import Link from "next/link";
import {
  getCurrency,
  getPurse,
  isElevatedRound,
  isPossibleToJoin,
} from "../../utils/tournaments";
import { Tournament } from "@/types/tournament";
import { useUI } from "../../context/UIContext";
import { useEffect, useState } from "react";
import Cta from "../Cta";
import { getTournamentPath } from "@/utils/tournament-path";

interface TournamentCardProps {
  tournament: Tournament;
}

const TournamentCard = ({ tournament }: TournamentCardProps) => {
  const {
    id: tournamentId,
    sponsors,
    name,
    rounds,
    startDate,
    endDate,
    participants,
    season,
    signUpStartTime,
    signUpEndTime,
    locationsConfiguration,
  } = tournament;
  const courseImg = rounds?.[0]?.course?.image?.url;

  const logo = sponsors?.[0]?.logoUri;
  const sponsorsName = sponsors?.[0]?.name;
  const playersCount =
    participants?.allInvitationsCount -
    participants?.declinedCount -
    participants?.withdrawnCount;

  const [nextSignupUri, setNextSignupUri] = useState<string | null>("");
  const { state: uiState } = useUI();

  useEffect(() => {
    if (nextSignupUri !== window.localStorage.getItem("next_signup_uri")) {
      setNextSignupUri(window.localStorage.getItem("next_signup_uri"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState]);

  const { dispatch } = useUI();

  const { isDesktop, isMobile } = useWindowSize();

  const signupUri = locationsConfiguration[0].paymentUrl;

  const isElevated = isElevatedRound(season, name);
  const isFinalSeason2 = season === 2 && name?.toLowerCase()?.includes("final");

  const tournamentHref = getTournamentPath(tournament);

  const signupStart = dayjs(signUpStartTime);
  const signupEnd = dayjs(signUpEndTime);
  const currentDate = dayjs();

  return (
    <motion.div className="w-full md:max-w-[500px]">
      <Link href={tournamentHref} className="hover:opacity-100">
        <div className="relative rounded-[10px] overflow-hidden flex flex-col w-full">
          <motion.div
            className="relative rounded-[10px] overflow-hidden h-[350px] lg:h-[370px] 2xl:h-[462px]"
            initial="initial"
            whileHover={isDesktop ? "hover" : "initial"}
            variants={{
              initial: { scale: 1 },
              hover: { scale: 1.05 },
            }}
            transition={{
              ease: "linear",
            }}
          >
            <div className="relative w-full h-full after:content-[''] after:absolute after:w-full after:h-full after:bg-gradient-to-t after:from-black/70 after:to-black/0">
              {courseImg && (
                <Image
                  src={courseImg}
                  fill
                  sizes="100vw"
                  className="object-cover"
                  alt={rounds?.[0]?.course?.displayName || "tournament image"}
                />
              )}
            </div>
            <div className="absolute top-0 z-[1] w-full h-full flex flex-col items-center justify-between pt-[30px] px-6 pb-7 2xl:px-8 2xl:pb-11">
              <motion.div
                className="flex flex-col items-center"
                initial={{
                  transform: isMobile
                    ? "translate(0, 20px)"
                    : "translate(0, 0)",
                }}
                whileInView={{
                  transform: "translate(0, 0)",
                }}
                transition={{
                  ease: "linear",
                  duration: 0.8,
                }}
              >
                {!!logo ? (
                  <div className="relative flex justify-center items-center rounded-full border border-tm-c-light w-[75px] h-[75px] lg:w-[89px] lg:h-[89px]">
                    <div className="relative w-[85%] h-[85%]">
                      <Image
                        src={logo}
                        fill
                        sizes="100vw"
                        className="object-contain"
                        alt={sponsorsName || "tournament logo"}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="bg-tm-c-light rounded-full w-[75px] h-[75px] lg:w-[89px] lg:h-[89px]" />
                )}
                <h2 className="text-xl leading-[1.5] tracking-[0.6px] mt-[15px] text-center lg:mt-6 2xl:text-[25px] 2xl:tracking-[0.75px]">
                  {name}
                </h2>
              </motion.div>
              <div className="grid grid-cols-2 w-full gap-2.5 md:w-[80%] lg:w-full">
                <div className="relative rounded-[10px] bg-[rgba(20,20,20,0.5)] px-2.5 py-6 text-center overflow-hidden">
                  {isElevated && (
                    <div
                      className="absolute top-0 -left-2.5 h-full w-full bg-auto overflow-hidden lg:left-0"
                      style={{
                        background: `url("/bg_graphics/arrows_on_off_pro.svg") no-repeat`,
                      }}
                    ></div>
                  )}

                  <h3 className="m-0 text-[15px] leading-[1.6] tracking-[0.45px]">
                    Purse
                  </h3>
                  <h2 className="m-0 text-xl leading-[1.5] tracking-[0.6px]">
                    {`${getCurrency(season, name)}${getPurse(
                      season,
                      tournamentId,
                      name,
                      playersCount,
                    ).toLocaleString("en-US")}`}
                  </h2>
                </div>
                <div className="relative rounded-[10px] bg-[rgba(20,20,20,0.5)] px-2.5 py-6 text-center overflow-hidden">
                  <h3 className="m-0 text-[15px] leading-[1.6] tracking-[0.45px]">
                    Players
                  </h3>
                  <h2 className="m-0 text-xl leading-[1.5] tracking-[0.6px]">
                    {isFinalSeason2 ? 100 : `${playersCount} / 1000`}
                  </h2>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Link>
      <div className="relative mt-1.5">
        <h2 className="m-0 text-base 2xl:text-xl">
          {dayjs(startDate).format("MMM D")} - {dayjs(endDate).format("MMM D")}
        </h2>
        {signUpStartTime &&
          signUpEndTime &&
          currentDate.isAfter(signupStart) &&
          currentDate.isBefore(signupEnd) &&
          isPossibleToJoin(playersCount) &&
          signupUri && (
            <Cta
              name="Join"
              className="!m-0 !text-base 2xl:!text-xl !text-tm-c-brand !bg-transparent !border-none !p-0"
              onClick={() => {
                window.localStorage.setItem("next_signup_uri", signupUri);
                dispatch({
                  type: "toggleJoinFlowInfoModal",
                  payload: { isOpen: true, title: name, modalType: "signup" },
                });
              }}
            />
          )}
      </div>
    </motion.div>
  );
};

export default TournamentCard;
